import React, { createContext, useContext, useEffect, useRef } from 'react';
import logger from '../utils/logger';

const LoggerContext = createContext(logger);

/**
 * LoggerProvider - Provides logging functionality throughout the app
 *
 * Usage:
 *   const log = useLogger();
 *   log.info('User logged in', { userId: user.id });
 *   log.error('API call failed', { endpoint: '/api/search', error: err.message });
 */
export const LoggerProvider = ({ children }) => {
  const loggerRef = useRef(logger);

  useEffect(() => {
    // Log app initialization
    loggerRef.current.info('VideBits frontend initialized', {
      environment: process.env.NODE_ENV,
      apiUrl: process.env.REACT_APP_API_URL
    });

    // Set up global error handler
    const handleError = (event) => {
      loggerRef.current.error('Uncaught error', {
        message: event.error?.message || event.message,
        stack: event.error?.stack,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      });
    };

    // Set up unhandled promise rejection handler
    const handleRejection = (event) => {
      loggerRef.current.error('Unhandled promise rejection', {
        reason: event.reason?.message || String(event.reason),
        stack: event.reason?.stack
      });
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);

      // Flush logs on unmount
      loggerRef.current.flush();
    };
  }, []);

  return (
    <LoggerContext.Provider value={loggerRef.current}>
      {children}
    </LoggerContext.Provider>
  );
};

/**
 * useLogger hook - Access the logger from any component
 *
 * @returns {Logger} Logger instance
 */
export const useLogger = () => {
  const context = useContext(LoggerContext);
  if (!context) {
    throw new Error('useLogger must be used within a LoggerProvider');
  }
  return context;
};

export default LoggerContext;
